import {
  pgTable,
  text,
  integer,
  timestamp,
  uuid,
  numeric,
  jsonb,
  pgEnum,
  doublePrecision,
  boolean,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { validationSchemas } from "./address-validation";

export const quoteStatusEnum = pgEnum("quote_status_type", [
  "ready_to_send",
  "pending_customer_approval",
  "accepted",
  "rejected",
  "scheduled_as_job",
]);

export const jobStatusEnum = pgEnum("job_status_type", [
  "scheduled",
  "in_progress",
  "completed",
  "cancelled",
]);

export const userRoleEnum = pgEnum("user_role_type", ["owner", "user"]);

export const customerTypeEnum = pgEnum("customer_type", ["person", "business"]);

// Product-related enums
export const productAvailabilityEnum = pgEnum("product_availability", [
  "in_stock",
  "low_stock",
  "out_of_stock",
  "discontinued",
  "special_order",
]);

export const pricingSourceEnum = pgEnum("pricing_source", [
  "hajoca_api",
  "manual_entry",
  "bulk_import",
  "quote_snapshot",
]);

export const orderStatusEnum = pgEnum("order_status_type", [
  "draft",
  "submitted",
]);

export const fulfillmentTypeEnum = pgEnum("fulfillment_type", [
  "delivery",
  "pickup",
]);

export const orgs = pgTable("orgs", {
  uid: text("uid").primaryKey().notNull(),
  name: text("name").notNull(),
  url: text("url"),
  logo: text("logo"),
  operating_hours: text("operating_hours"),
  tagline: text("tagline"),
  description: text("description"),
  // Hajoca integration fields
  hajocaCustomerId: text("hajoca_customer_id"),
  hajocaProfitCenter: text("hajoca_profit_center"),
  // Contact information
  phone: text("phone"),
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  country: text("country").default("US"),
});

export const users = pgTable("users", {
  uid: text("uid").primaryKey(), // This is the Clerk user ID
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  phone: text("phone").notNull(),
  email: text("email").notNull(),
  orgId: text("org_id").references(() => orgs.uid),
  role: userRoleEnum("role").notNull(),
});

export const customers = pgTable("customers", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  customerType: customerTypeEnum("customer_type").notNull().default("person"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  businessName: text("business_name"),
  phone: text("phone"),
  email: text("email"),
  orgId: text("org_id")
    .notNull()
    .references(() => orgs.uid),
  streetAddress: text("street_address"),
  streetAddress2: text("street_address_2"),
  city: text("city"),
  state: text("state"),
  zipCode: text("zip_code"),
  country: text("country").default("US"),
});

export const quotes = pgTable("quotes", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  customerId: uuid("customer_id")
    .notNull()
    .references(() => customers.uid),
  quoteStatus: quoteStatusEnum("quote_status")
    .notNull()
    .default("ready_to_send"),
  markupPercent: doublePrecision("markup_percent"),
  partsCostPremarkup: integer("parts_cost_premarkup"),
  partsCostPostmarkup: integer("parts_cost_postmarkup"),
  partsAffectPrice: boolean("parts_affect_price").default(true),
  taxAmount: integer("tax_amount").notNull(),
  notes: text("notes"),
  lineItems: text("line_items"),
  laborHours: doublePrecision("labor_hours"),
  laborRate: integer("labor_rate"),
  customerApprovalDate: timestamp("customer_approval_date", {
    withTimezone: true,
  }),
  customerApprovalSignature: text("customer_approval_signature"),
  orgId: text("org_id")
    .notNull()
    .references(() => orgs.uid),
  amountPrediscount: integer("amount_prediscount").notNull(),
  amountPresalesTax: integer("amount_presales_tax").notNull(),
  amountPostsalesTax: integer("amount_postsales_tax").notNull(),
  title: text("title").notNull().default(""),
  description: text("description").notNull().default(""),
  quoteServices: jsonb("quote_services")
    .array()
    .default(sql`'{}'`),
  quoteCustomParts: jsonb("quote_custom_parts")
    .array()
    .default(sql`'{}'`),
  creationTime: timestamp("creation_time", { withTimezone: true }),
  discountPercentage: doublePrecision("discount_percentage"),
  discountDescription: text("discount_description"),
  approval_code: text("approval_code"),
  approval_code_created_at: timestamp("approval_code_created_at", {
    withTimezone: true,
  }),
  approval_code_expires_at: timestamp("approval_code_expires_at", {
    withTimezone: true,
  }),
  approval_code_used_at: timestamp("approval_code_used_at", {
    withTimezone: true,
  }),
  approval_code_is_active: boolean("approval_code_is_active").default(false),
  quoteNumber: text("quote_number").notNull(),
  products: jsonb("products").default(sql`'[]'`),

  // Price locking: when quote is sent for approval, product prices are embedded
  pricesLockedAt: timestamp("prices_locked_at", { withTimezone: true }),

  createdByUserId: text("created_by_user_id"),
  createdByName: text("created_by_name"),
});

export const quoteTemplates = pgTable("quote_templates", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  orgId: text("org_id").references(() => orgs.uid),
  laborHours: doublePrecision("labor_hours"),
  markupPercent: doublePrecision("markup_percent"),
  partsCostPremarkup: integer("parts_cost_premarkup"),
  partsCostPostmarkup: integer("parts_cost_postmarkup"),
  partsAffectPrice: boolean("parts_affect_price").default(true),
  name: text("name").notNull().default(""),
  description: text("description").notNull().default(""),
  defaultNotes: text("default_notes").notNull().default(""),
  quoteServices: jsonb("quote_services")
    .array()
    .default(sql`'[]'`),
  quoteCustomParts: jsonb("quote_custom_parts")
    .array()
    .default(sql`'[]'`),
  products: jsonb("products").default(sql`'[]'`),
  laborRate: integer("labor_rate"),
  discountPercentage: doublePrecision("discount_percentage"),
  discountDescription: text("discount_description"),
  amountPrediscount: integer("amount_prediscount").notNull(),
  amountPresalesTax: integer("amount_presales_tax").notNull(),
  creationTime: timestamp("creation_time", { withTimezone: true }),
  createdByUserId: text("created_by_user_id"),
  createdByName: text("created_by_name"),
});

export const services = pgTable("services", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  title: text("title").notNull(),
  description: text("description"),
  orgId: text("org_id")
    .notNull()
    .references(() => orgs.uid),
  cost: integer("cost").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const customParts = pgTable("custom_parts", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  title: text("title").notNull(),
  orgId: text("org_id")
    .notNull()
    .references(() => orgs.uid),
  cost: integer("cost").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
});

export const orders = pgTable("orders", {
  uid: uuid("uid").primaryKey().defaultRandom(),

  // Core order information
  orderNumber: text("order_number").unique(),
  orderStatus: orderStatusEnum("order_status").notNull().default("draft"),

  // Relationships
  orgId: text("org_id")
    .notNull()
    .references(() => orgs.uid),
  quoteId: uuid("quote_id").references(() => quotes.uid), // Optional: if created from quote

  // Order details
  title: text("title").notNull(),
  description: text("description").notNull(),
  fulfillmentType: fulfillmentTypeEnum("fulfillment_type")
    .notNull()
    .default("delivery"),
  notes: text("notes"),

  // Products (multiple items per order)
  products: jsonb("products")
    .$type<OrderProduct[]>()
    .default(sql`'[]'`), // Array of OrderProduct objects

  // Dates
  orderDate: timestamp("order_date", { withTimezone: true }).notNull(),
  expectedDeliveryDate: timestamp("expected_delivery_date", {
    withTimezone: true,
  }),
  receivedDate: timestamp("received_date", { withTimezone: true }),

  // Financial (stored in cents as integer)
  totalOrderCost: integer("total_order_cost"),
  // Estimated sales tax snapshot from checkout; null = no estimate available.
  // Not included in totalOrderCost, which remains the merchandise subtotal.
  taxAmount: integer("tax_amount"),

  // External integration
  hajocaInvoiceId: text("hajoca_invoice_id"),
  hajocaOrderId: text("hajoca_order_id"),
  orderConfirmation: text("order_confirmation"),
  submittedAt: timestamp("submitted_at", { withTimezone: true }),

  // Audit fields
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),

  // Created by tracking
  createdByUserId: text("created_by_user_id"),
  createdByName: text("created_by_name"),
});

export const orderTemplates = pgTable(
  "order_templates",
  {
    uid: uuid("uid").primaryKey().defaultRandom(),

    // Relationships
    orgId: text("org_id")
      .notNull()
      .references(() => orgs.uid),

    // Template core data (what gets filled in from template)
    name: text("name").notNull(),
    description: text("description").default(""),
    products: jsonb("products")
      .$type<TemplateProduct[]>()
      .default(sql`'[]'`), // Array of TemplateProduct objects

    // Template metadata
    usageCount: integer("usage_count").default(0),
    lastUsedAt: timestamp("last_used_at", { withTimezone: true }),

    // Audit fields
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),

    // Creator tracking
    createdByUserId: text("created_by_user_id"),
    createdByName: text("created_by_name"),
  },
  (table) => ({
    orgIdIdx: index("order_templates_org_id_idx").on(table.orgId),
    usageCountIdx: index("order_templates_usage_count_idx").on(
      table.usageCount,
    ),
    lastUsedIdx: index("order_templates_last_used_idx").on(table.lastUsedAt),
  }),
);

export const jobs = pgTable("jobs", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  customer_id: uuid("customer_id").references(() => customers.uid),
  quote_id: uuid("quote_id").references(() => quotes.uid),
  order_id: uuid("order_id").references(() => orders.uid),
  employee_id: text("employee_id").references(() => users.uid),
  description: text("description"),
  status: jobStatusEnum("status"),
  notes: text("notes"),
  before_photos: jsonb("before_photos").default("[]"),
  after_photos: jsonb("after_photos").default("[]"),
  scheduled_time: timestamp("scheduled_time", { withTimezone: true }),
  jobNumber: text("job_number"),
  orgId: text("org_id")
    .notNull()
    .references(() => orgs.uid),
});

export const apiKeys = pgTable("api_keys", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  keyName: text("key_name").notNull(),
  keyValue: text("key_value").notNull().unique(),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at", { withTimezone: true })
    .defaultNow()
    .notNull(),
  lastUsed: timestamp("last_used", { withTimezone: true }),
  createdBy: text("created_by"), // Optional: track who created the key
  notes: text("notes"), // Optional: description of what the key is used for
});

export const insertOrgSchema = createInsertSchema(orgs, {
  name: z.string().min(1, "Organization name is required"),
  hajocaCustomerId: z.string().optional(),
  hajocaProfitCenter: z.string().optional(),
  phone: z.string().optional(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
  country: z.string().default("US"),
}).pick({
  name: true,
  url: true,
  logo: true,
  operating_hours: true,
  tagline: true,
  description: true,
  hajocaCustomerId: true,
  hajocaProfitCenter: true,
  phone: true,
  addressLine1: true,
  addressLine2: true,
  city: true,
  state: true,
  zipCode: true,
  country: true,
});

export const insertUserSchema = createInsertSchema(users).pick({
  uid: true,
  firstName: true,
  lastName: true,
  phone: true,
  email: true,
  role: true,
  orgId: true,
});

export const insertCustomerSchema = createInsertSchema(customers, {
  customerType: z.enum(["person", "business"]).default("person"),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  businessName: z.string().optional(),
  streetAddress: validationSchemas.streetAddress,
  city: validationSchemas.city,
  state: validationSchemas.state,
  zipCode: validationSchemas.zipCode,
  phone: validationSchemas.phone,
  email: validationSchemas.email,
})
  .pick({
    customerType: true,
    firstName: true,
    lastName: true,
    businessName: true,
    email: true,
    phone: true,
    streetAddress: true,
    streetAddress2: true,
    city: true,
    state: true,
    zipCode: true,
    country: true,
  })
  .superRefine((data, ctx) => {
    if (data.customerType === "person") {
      if (!data.firstName || data.firstName.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "First name is required for person customers",
          path: ["firstName"],
        });
      }
      if (!data.lastName || data.lastName.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Last name is required for person customers",
          path: ["lastName"],
        });
      }
    } else if (data.customerType === "business") {
      if (!data.businessName || data.businessName.trim() === "") {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Business name is required for business customers",
          path: ["businessName"],
        });
      }
    }
  });

// Quote product schema for JSON validation
// Supports both draft (ID + quantity only) and locked (embedded pricing) states
export const quoteProductSchema = z.object({
  productId: z.string().uuid("Invalid product ID"),
  quantity: z.number().int().min(1, "Quantity must be at least 1"),
  notes: z.string().optional(),

  // Embedded pricing fields (optional - only present when quote is locked)
  priceAtLock: z.number().int().optional(), // Price in cents at time of locking
  description: z.string().optional(), // Product description snapshot
  hajocaProductId: z.string().optional(), // Hajoca ID snapshot
  postMarkupUnitPrice: z.number().int().optional(), // Unit price after markup in cents
  postMarkupTotalPrice: z.number().int().optional(), // Total = unit price × quantity in cents
});

export const quoteCustomPartSchema = z.object({
  customPartId: z.string().uuid("Invalid custom part ID"),
  quantity: z.number().int().min(1, "Quantity must be at least 1"),
  customPrice: z
    .number()
    .int()
    .min(0, "Custom price must be non-negative")
    .optional(), // Store in cents like products
  customName: z.string().optional(),
});

export const orderProductSchema = z.object({
  productId: z.string().uuid("Invalid product ID"),
  hajocaProductId: z.string().min(1, "Hajoca Product ID is required"),
  description: z.string().min(1, "Description is required"),
  quantity: z.number().int().min(1, "Quantity must be at least 1"),
  unitPrice: z.number().min(0, "Unit price must be non-negative"),
  totalPrice: z.number().min(0, "Total price must be non-negative"),
  quantityReceived: z
    .number()
    .int()
    .min(0, "Quantity received must be non-negative")
    .default(0),
  notes: z.string().optional(),
});

// Template product schema (simpler than OrderProduct - just configuration data)
export const templateProductSchema = z.object({
  productId: z.string().uuid("Invalid product ID"),
  hajocaProductId: z.string().min(1, "Hajoca Product ID is required"),
  description: z.string().min(1, "Description is required"),
  quantity: z.number().int().min(1, "Quantity must be at least 1"),
  notes: z.string().optional(),
  // Note: No unitPrice (pricing is dynamic), no totalPrice, no quantityReceived
});

// Export the type early so it can be used in table definitions
export type OrderProduct = z.infer<typeof orderProductSchema>;
export type TemplateProduct = z.infer<typeof templateProductSchema>;

// Simplified product structure for nononsemble orders pipeline
export interface NononsembleOrderProduct {
  productId?: string | null; // Could be null if not in our products table
  hajocaProductId: string;
  quantity: number;
}

export const insertQuoteSchema = createInsertSchema(quotes, {
  customerId: z
    .string()
    .min(1, "Please select a customer")
    .uuid("Please select a customer"),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  notes: z.string().optional(),
  amountPrediscount: z.number().min(0),
  amountPresalesTax: z.number().min(0),
  amountPostsalesTax: z.number().min(0),
  taxAmount: z.number().min(0),
  laborHours: z.number().min(0).max(9999.99).optional(),
  laborRate: z.number().min(0).optional(),
  markupPercent: z.number().min(0).optional(),
  partsCostPremarkup: z.number().min(0).default(0),
  partsCostPostmarkup: z.number().min(0).default(0),
  partsAffectPrice: z.boolean().default(true),
  discountPercentage: z.number().min(0).optional(),
  discountDescription: z.string().optional(),
  products: z.array(quoteProductSchema).default([]),
  quoteServices: z.any().array().default([]), // JSONB array - validated by OpenAPI schema
  quoteCustomParts: z.array(quoteCustomPartSchema).default([]),
  orgId: z.string().min(1, "Organization ID is required"),
}).pick({
  customerId: true,
  title: true,
  description: true,
  notes: true,
  amountPrediscount: true,
  amountPresalesTax: true,
  amountPostsalesTax: true,
  taxAmount: true,
  laborHours: true,
  laborRate: true,
  markupPercent: true,
  partsCostPremarkup: true,
  partsCostPostmarkup: true,
  partsAffectPrice: true,
  discountPercentage: true,
  discountDescription: true,
  products: true,
  quoteServices: true,
  quoteCustomParts: true,
  quoteStatus: true,
});

export const insertServiceSchema = createInsertSchema(services).pick({
  title: true,
  cost: true,
});

export const insertCustomPartSchema = createInsertSchema(customParts).pick({
  title: true,
  cost: true,
});

export const insertQuoteTemplateSchema = createInsertSchema(quoteTemplates, {
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
  laborHours: z.number().min(0).max(9999.99).optional(),
  laborRate: z.number().min(0).optional(),
  markupPercent: z.number().min(0).optional(),
  partsCostPremarkup: z.number().min(0).default(0),
  partsCostPostmarkup: z.number().min(0).default(0),
  partsAffectPrice: z.boolean().default(true),
  discountPercentage: z.number().min(0).optional(),
  discountDescription: z.string().optional(),
  defaultNotes: z.string().optional(),
  amountPrediscount: z.number().min(0).default(0),
  amountPresalesTax: z.number().min(0).default(0),
  orgId: z.string().min(1, "Organization ID is required"),
}).pick({
  name: true,
  description: true,
  laborHours: true,
  laborRate: true,
  markupPercent: true,
  partsCostPremarkup: true,
  partsCostPostmarkup: true,
  partsAffectPrice: true,
  discountPercentage: true,
  discountDescription: true,
  defaultNotes: true,
  amountPrediscount: true,
  amountPresalesTax: true,
  quoteServices: true,
  quoteCustomParts: true,
});

export type Org = typeof orgs.$inferSelect;
export type InsertOrg = z.infer<typeof insertOrgSchema>;
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type Customer = typeof customers.$inferSelect;
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;
export type Quote = typeof quotes.$inferSelect;
export type InsertQuote = z.infer<typeof insertQuoteSchema>;
export type QuoteProduct = z.infer<typeof quoteProductSchema>;
export type QuoteCustomPart = z.infer<typeof quoteCustomPartSchema>;
export type Service = typeof services.$inferSelect;
export type InsertService = z.infer<typeof insertServiceSchema>;
export type CustomPart = typeof customParts.$inferSelect;
export type InsertCustomPart = z.infer<typeof insertCustomPartSchema>;
export type QuoteTemplate = typeof quoteTemplates.$inferSelect;
export type InsertQuoteTemplate = z.infer<typeof insertQuoteTemplateSchema>;
export type Order = typeof orders.$inferSelect;
export type Job = typeof jobs.$inferSelect;

// ========================================
// PRODUCT CATALOG TABLES
// ========================================

export const products = pgTable("products", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  
  // External identifiers
  hajocaProductId: text("hajoca_product_id").notNull(),
  sku: text("sku"),
  
  // New: Product identifiers from CSV
  name: text("name"),
  catalogNo: text("catalog_no"), 
  upc: text("upc"),
  
  // Product information
  description: text("description").notNull(),
  aiDescription: text("ai_description"),
  category: text("category"), // Made optional
  manufacturer: text("manufacturer"),
  unitOfMeasure: text("unit_of_measure").default("EA"),
  hajocaProfitCenter: text("hajoca_profit_center").notNull(),
  
  // New: Product media and metadata
  imageUrl: text("image_url"),
  searchKeywords: text("search_keywords"),
  eclipseKeywords: text("eclipse_keywords"),
  webDescription: text("web_description"),
  status: text("status"),

  // Search vocabulary classification
  entity: text("entity"),
  materials: text("materials").array().notNull().default([]),
  manufacturerCleaned: text("manufacturer_cleaned"),
  
  // New: Quantity fields from CSV
  sellQty: integer("sell_qty"),
  pkgQty: integer("pkg_qty"),
  pricingQty: integer("pricing_qty"),
  branches: jsonb("branches").$type<{ [key: string]: unknown } | null>(),

  // Additional/unmapped CSV fields stored as flexible JSONB
  extraData: jsonb("extra_data").$type<{ [key: string]: string | boolean | number } | undefined>(),

  // Current cached pricing (for fast lookups, stored in cents as integer)
  currentPrice: integer("current_price"),
  currentAvailability: productAvailabilityEnum("current_availability"),
  currentQuantityAvailable: integer("current_quantity_available"),
  pricingLastUpdated: timestamp("pricing_last_updated", { withTimezone: true }),
  
  // Business data
  isActive: boolean("is_active").default(true),
  
  // Audit fields
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  // Composite key unique constraint (required for upsert conflict resolution)
  compositeKey: uniqueIndex("idx_products_hajoca_id_profit_center").on(table.hajocaProductId, table.hajocaProfitCenter),
  hajocaProfitCenterIdx: index("products_hajoca_profit_center_idx").on(table.hajocaProfitCenter),
}));

export const productPricingHistory = pgTable("product_pricing_history", {
  uid: uuid("uid").primaryKey().defaultRandom(),

  // Product reference
  productId: uuid("product_id")
    .notNull()
    .references(() => products.uid, { onDelete: "cascade" }),
  hajocaProductId: text("hajoca_product_id").notNull(),

  // Pricing data
  price: numeric("price", { precision: 10, scale: 2 }),
  availability: productAvailabilityEnum("availability"),
  quantityAvailable: integer("quantity_available"),

  // Temporal data
  effectiveDate: timestamp("effective_date", { withTimezone: true }).notNull(),
  source: pricingSourceEnum("source").notNull(),

  // Audit
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});

// Organization-specific product pricing table
export const orgProductPricing = pgTable(
  "org_product_pricing",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orgId: text("org_id")
      .notNull()
      .references(() => orgs.uid, { onDelete: "cascade" }),
    productId: uuid("product_id")
      .notNull()
      .references(() => products.uid, { onDelete: "cascade" }),
    hajocaCustomerId: text("hajoca_customer_id").notNull(),
    hajocaProductId: text("hajoca_product_id").notNull(),
    hajocaProfitCenter: text("hajoca_profit_center").notNull(), // Add profit center for debugging and filtering
    priceCents: integer("price_cents"), // Price in cents to match frontend expectations
    availability: productAvailabilityEnum("availability"),
    quantityAvailable: integer("quantity_available"),
    lastUpdated: timestamp("last_updated", { withTimezone: true }).defaultNow(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    // Unique constraint: one pricing record per org per product
    orgProductUnique: index("org_product_pricing_org_product_unique").on(
      table.orgId,
      table.productId,
    ),
  }),
);

// Recent products table for tracking products purchased in last 12 months
export const recentProducts = pgTable("recent_products", {
  uid: uuid("uid").primaryKey().defaultRandom(),
  orgId: text("org_id").notNull().references(() => orgs.uid, { onDelete: "cascade" }),
  productId: uuid("product_id").references(() => products.uid, { onDelete: "cascade" }),
  hajocaProductId: text("hajoca_product_id").notNull(),
  hajocaProfitCenter: text("hajoca_profit_center").notNull(),
  purchaseDate: timestamp("purchase_date", { withTimezone: true }).notNull(),
  quantity: numeric("quantity").notNull(),
  
  // Denormalized product data for fast queries (matching products table)
  sku: text("sku"),
  name: text("name"),
  catalogNo: text("catalog_no"),
  upc: text("upc"),
  description: text("description").notNull(),
  aiDescription: text("ai_description"),
  category: text("category"), // Made optional to match products table
  manufacturer: text("manufacturer"),
  unitOfMeasure: text("unit_of_measure").default("EA"),
  imageUrl: text("image_url"),
  searchKeywords: text("search_keywords"),
  eclipseKeywords: text("eclipse_keywords"),
  webDescription: text("web_description"),
  status: text("status"),
  sellQty: integer("sell_qty"),
  pkgQty: integer("pkg_qty"),
  pricingQty: integer("pricing_qty"),
  branches: jsonb("branches").$type<{ [key: string]: unknown } | null>(),

  // Additional/unmapped CSV fields stored as flexible JSONB
  extraData: jsonb("extra_data").$type<{ [key: string]: string | boolean | number } | undefined>(),

  // Pricing and availability data (aligned with products table schema)
  currentPrice: integer("current_price"),
  currentAvailability: productAvailabilityEnum("current_availability"),
  currentQuantityAvailable: integer("current_quantity_available"),
  pricingLastUpdated: timestamp("pricing_last_updated", { withTimezone: true }),
  isActive: boolean("is_active").default(true),
  
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (table) => ({
  orgIdIdx: index("idx_recent_products_org_id").on(table.orgId),
  productIdIdx: index("idx_recent_products_product_id").on(table.productId),
  purchaseDateIdx: index("idx_recent_products_purchase_date").on(table.purchaseDate),
}));

// Nononsemble orders table for pipeline data
export const nononsembleOrders = pgTable(
  "nononsemble_orders",
  {
    uid: uuid("uid").primaryKey().defaultRandom(),
    orgId: text("org_id")
      .notNull()
      .references(() => orgs.uid),
    hajocaOrderId: text("hajoca_order_id"),
    providedDate: timestamp("provided_date", { withTimezone: true }).notNull(),
    products: jsonb("products")
      .$type<NononsembleOrderProduct[]>()
      .default(sql`'[]'`), // Array of simplified product objects
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  },
  (table) => ({
    orgIdIdx: index("idx_nononsemble_orders_org_id").on(table.orgId),
    hajocaOrderIdIdx: index("idx_nononsemble_orders_hajoca_order_id").on(
      table.hajocaOrderId,
    ),
    providedDateIdx: index("idx_nononsemble_orders_provided_date").on(
      table.providedDate,
    ),
  }),
);

// ========================================
// PRODUCT VALIDATION SCHEMAS
// ========================================

export const insertProductSchema = createInsertSchema(products, {
  hajocaProductId: z.string().min(1, "Hajoca Product ID is required"),
  description: z.string().min(1, "Description is required"),
  category: z.string().min(1, "Category is required"),
  manufacturer: z.string().optional(),
  unitOfMeasure: z.string().default("EA"),
  hajocaProfitCenter: z.string().min(1, "Hajoca Profit Center is required"),
  currentPrice: z.coerce.number().int().positive().optional(),
  currentQuantityAvailable: z.number().int().min(0).optional(),
}).pick({
  hajocaProductId: true,
  sku: true,
  description: true,
  category: true,
  manufacturer: true,
  unitOfMeasure: true,
  hajocaProfitCenter: true,
  currentPrice: true,
  currentAvailability: true,
  currentQuantityAvailable: true,
  isActive: true,
});

export const insertProductPricingHistorySchema = createInsertSchema(
  productPricingHistory,
  {
    productId: z.string().uuid("Invalid product ID"),
    hajocaProductId: z.string().min(1, "Hajoca Product ID is required"),
    price: z.coerce.number().positive().optional(),
    quantityAvailable: z.number().int().min(0).optional(),
    effectiveDate: z.date(),
    source: z.enum([
      "hajoca_api",
      "manual_entry",
      "bulk_import",
      "quote_snapshot",
    ]),
  },
).pick({
  productId: true,
  hajocaProductId: true,
  price: true,
  availability: true,
  quantityAvailable: true,
  effectiveDate: true,
  source: true,
});

export const insertOrgProductPricingSchema = createInsertSchema(
  orgProductPricing,
  {
    orgId: z.string().min(1, "Organization ID is required"),
    productId: z.string().uuid("Invalid product ID"),
    hajocaCustomerId: z.string().min(1, "Hajoca Customer ID is required"),
    hajocaProductId: z.string().min(1, "Hajoca Product ID is required"),
    hajocaProfitCenter: z.string().min(1, "Hajoca Profit Center is required"),
    priceCents: z
      .number()
      .int()
      .min(0, "Price must be non-negative")
      .optional(),
    quantityAvailable: z
      .number()
      .int()
      .min(0, "Quantity must be non-negative")
      .optional(),
  },
).pick({
  orgId: true,
  productId: true,
  hajocaCustomerId: true,
  hajocaProductId: true,
  hajocaProfitCenter: true,
  priceCents: true,
  availability: true,
  quantityAvailable: true,
});

export const insertRecentProductsSchema = createInsertSchema(recentProducts, {
  orgId: z.string().min(1, "Organization ID is required"),
  productId: z.string().uuid("Invalid product ID").optional(),
  hajocaProductId: z.string().min(1, "Hajoca Product ID is required"),
  hajocaProfitCenter: z.string().min(1, "Hajoca Profit Center is required"),
  purchaseDate: z.date(),
  quantity: z.coerce.number().positive("Quantity must be positive"),
  sku: z.string().optional(),
  description: z.string().optional(),
  aiDescription: z.string().optional(),
  category: z.string().optional(),
  manufacturer: z.string().optional(),
  unitOfMeasure: z.string().default("EA"),
  currentPrice: z.number().int().min(0).optional(),
  currentAvailability: z.string().default("in_stock"),
  currentQuantityAvailable: z.number().int().min(0).optional(),
  pricingLastUpdated: z.date().optional(),
  isActive: z.boolean().default(true),
}).pick({
  orgId: true,
  productId: true,
  hajocaProductId: true,
  hajocaProfitCenter: true,
  purchaseDate: true,
  quantity: true,
  sku: true,
  description: true,
  aiDescription: true,
  category: true,
  manufacturer: true,
  unitOfMeasure: true,
  currentPrice: true,
  currentAvailability: true,
  currentQuantityAvailable: true,
  pricingLastUpdated: true,
  isActive: true,
});

export const insertOrderSchema = createInsertSchema(orders, {
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  notes: z.string().optional(),
  products: z.array(orderProductSchema).default([]),
  totalOrderCost: z
    .number()
    .min(0, "Total cost must be non-negative")
    .optional(),
  taxAmount: z
    .number()
    .int()
    .min(0, "Tax amount must be non-negative")
    .nullable()
    .optional(),
  orderDate: z.date(),
  expectedDeliveryDate: z.date().optional(),
  orgId: z.string().min(1, "Organization ID is required"),
}).pick({
  quoteId: true,
  title: true,
  description: true,
  notes: true,
  products: true,
  orderDate: true,
  expectedDeliveryDate: true,
  totalOrderCost: true,
  taxAmount: true,
  orderStatus: true,
  orgId: true,
});

// Draft order schema with relaxed validation for auto-saving
export const insertDraftOrderSchema = createInsertSchema(orders, {
  title: z.string().default("Draft Order"), // Allow empty, provide default
  description: z.string().default(""), // Allow empty
  notes: z.string().optional(),
  products: z.array(orderProductSchema).default([]),
  totalOrderCost: z
    .number()
    .min(0, "Total cost must be non-negative")
    .optional(),
  taxAmount: z
    .number()
    .int()
    .min(0, "Tax amount must be non-negative")
    .nullable()
    .optional(),
  orderDate: z.date().default(() => new Date()), // Auto-set current date
  expectedDeliveryDate: z.date().optional(),
  orgId: z.string().min(1, "Organization ID is required"),
  orderStatus: z.literal("draft"), // Enforce draft status
}).pick({
  quoteId: true,
  title: true,
  description: true,
  notes: true,
  products: true,
  orderDate: true,
  expectedDeliveryDate: true,
  totalOrderCost: true,
  taxAmount: true,
  orderStatus: true,
  orgId: true,
  createdByUserId: true,
  createdByName: true,
});

// ========================================
// PRODUCT TYPE EXPORTS
// ========================================

export type Product = typeof products.$inferSelect;
export type InsertProduct = z.infer<typeof insertProductSchema>;
export type ProductPricingHistory = typeof productPricingHistory.$inferSelect;
export type InsertProductPricingHistory = z.infer<
  typeof insertProductPricingHistorySchema
>;
export type OrgProductPricing = typeof orgProductPricing.$inferSelect;
export type InsertOrgProductPricing = z.infer<
  typeof insertOrgProductPricingSchema
>;
export type RecentProduct = typeof recentProducts.$inferSelect;
export type InsertRecentProduct = z.infer<typeof insertRecentProductsSchema>;
export type NononsembleOrder = typeof nononsembleOrders.$inferSelect;

export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type InsertDraftOrder = z.infer<typeof insertDraftOrderSchema>;

// Order Templates
export type OrderTemplate = typeof orderTemplates.$inferSelect;

export const insertOrderTemplateSchema = createInsertSchema(orderTemplates, {
  name: z
    .string()
    .min(1, "Template name is required")
    .max(255, "Template name must be less than 255 characters"),
  description: z
    .string()
    .max(1000, "Description must be less than 1000 characters"),
  products: z.array(templateProductSchema).default([]),
  orgId: z.string().min(1, "Organization ID is required"),
}).pick({
  name: true,
  description: true,
  products: true,
  // orgId, createdByUserId, createdByName added by server (matches insertOrderSchema pattern)
});

export type InsertOrderTemplate = z.infer<typeof insertOrderTemplateSchema>;

// ==== STATUS MANAGEMENT UTILITIES ====

// Extract status types from enums
export type QuoteStatus = (typeof quoteStatusEnum.enumValues)[number];
export type JobStatus = (typeof jobStatusEnum.enumValues)[number];
export type UserRole = (typeof userRoleEnum.enumValues)[number];
export type CustomerType = (typeof customerTypeEnum.enumValues)[number];

export type OrderStatus = (typeof orderStatusEnum.enumValues)[number];
export type FulfillmentType = (typeof fulfillmentTypeEnum.enumValues)[number];

// Centralized status color definitions
export const STATUS_COLORS = {
  // Quote statuses
  ready_to_send: "bg-gray-500",
  pending_customer_approval: "bg-blue-500",
  accepted: "bg-green-500",
  rejected: "bg-red-500",
  scheduled_as_job: "bg-purple-500",

  // Job statuses
  scheduled: "bg-blue-500",
  in_progress: "bg-yellow-500",
  completed: "bg-green-500",
  cancelled: "bg-red-500",

  // Order statuses (using consistent colors)
  draft: "bg-gray-500",
  submitted: "bg-green-500",

  // PC dashboard order states (derived from readyForPickupAt client-side)
  new: "bg-blue-500",
  ready_for_pickup: "bg-green-500",

  // in_progress, completed, cancelled: already defined above

  // User roles
  owner: "bg-purple-500",
  user: "bg-blue-500",
} as const;

// Status formatting function
export const formatStatus = (status: string): string => {
  if (!status) return "Unknown";

  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Specific formatters (allowing for future customization)
export const formatQuoteStatus = (status: string): string =>
  formatStatus(status);
export const formatJobStatus = (status: string): string => formatStatus(status);
export const formatOrderStatus = (status: string): string =>
  formatStatus(status);
export const formatUserRole = (role: string): string => formatStatus(role);

// Helper to get status color
export const getStatusColor = (status: string): string => {
  return STATUS_COLORS[status as keyof typeof STATUS_COLORS] || "bg-gray-500";
};

// Status badge props helper for components
export const getStatusBadgeProps = (
  status: string,
  formatter?: (status: string) => string,
) => ({
  status,
  statusColors: STATUS_COLORS,
  formatStatus: formatter || formatStatus,
});

// ========================================
// API EXTENSION TYPES
// ========================================

/**
 * Organization data for public quote viewing
 * Includes address and contact info from Clerk
 */
export interface OrganizationData {
  name?: string;
  imageUrl?: string;
  address?: {
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  } | null;
  phone?: string | null;
}

/**
 * Quote with organization data attached
 * Used for public quote approval pages where customer needs contractor info
 */
export interface QuoteWithOrganization extends Quote {
  organization: OrganizationData;
}
